import type { CSSProperties } from 'react'
import StackBadge from '@/components/StackBadge'
import type { SectionOf } from './shared'

/**
 * Each row scrolls itself, forever, at the same speed as its neighbours — so the
 * duration is derived from how many marks a lap passes rather than fixed. Four
 * seconds a mark is slow enough to read a logo as it goes by, and a lap is one
 * half of the track, so every row drifts at the same pixels per second however
 * many marks it holds. The keyframes and the reduced-motion fallback are
 * `.stack-marquee*` in `src/index.css`.
 */
const SECONDS_PER_ITEM = 4

/**
 * How many marks one half of the track must hold before it is wider than the
 * strip it drifts through — and this is the whole fix of 2026-09-15.
 *
 * The track shipped holding the row exactly twice and translating half of it,
 * which is seamless only while half a track is wider than the window. The
 * models row has eight marks and is; the applications row has seven and the
 * infrastructures row four, so the second copy ran out mid-window and the
 * reader got white space after "and many more" and then a jump when the
 * animation restarted. *"sadece connected AI models kısmında smooth bir akış
 * var"* — exactly the one row long enough.
 *
 * So each copy is repeated until it reaches this many marks, and the track is
 * two of those. Twelve is about 1,300px at this row height against a strip of
 * at most ~950px (`max-w-6xl` less the 176px label), which leaves room for a
 * wider viewport without going back to arithmetic on logo widths.
 */
const MARKS_PER_HALF = 12

export default function StackSection({ section }: { section: SectionOf<'stack'> }) {
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {section.groups.map((group) => {
        // Repeats per half, never below one, and the track is two halves. The
        // duration counts the marks in a half rather than in the row, so all
        // three still travel at the same pixels per second whatever it works
        // out to.
        const repeats = Math.max(1, Math.ceil(MARKS_PER_HALF / group.items.length))
        const marksPerHalf = repeats * group.items.length

        return (
        <div key={group.heading} className="flex flex-col items-center gap-3 sm:flex-row sm:items-center">
          {/* The label is centred against the strip beside it rather than pinned
              to its top: a heading that wraps to two lines (most of them do at
              176px, in both languages) would otherwise sit high of the marks.
              `min-h` rather than `h`, so a longer translation grows instead of
              overflowing, and it tracks `StackBadge`'s row height — 3.5rem since
              the bar was asked to be smaller on 2026-09-15. */}
          <h3 className="w-44 shrink-0 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground sm:flex sm:min-h-[3.5rem] sm:items-center sm:justify-end sm:text-right">
            {/* One word per line, on purpose (asked for 2026-08-28). These
                labels are two-word category names, and at this width two of the
                three wrapped by themselves while the short one stayed on a
                single line — so the column read as ragged. Breaking every label
                at its space makes all three the same two-line block whatever the
                locale. */}
            <span>
              {group.heading.split(' ').map((word) => (
                <span key={word} className="block">
                  {word}
                </span>
              ))}
            </span>
          </h3>
          {/* The row no longer wraps — it is a window onto a strip that moves.
              That is what removed the old breakpoint problem rather than working
              around it: eight marks beside a heading never fitted a phone, and
              wrapping to three lines was the least-bad answer until the row
              started drifting. `min-w-0` so the flex child may actually be
              narrower than its contents; without it the strip pushes the layout
              wide and nothing ever clips. */}
          <div className="stack-marquee relative w-full min-w-0 flex-1 overflow-hidden">
            <div
              className="stack-marquee-track flex w-max items-center"
              style={
                {
                  '--stack-marquee-duration': `${marksPerHalf * SECONDS_PER_ITEM}s`,
                } as CSSProperties
              }
            >
              {/* Two halves, because a loop needs the row already waiting where
                  the row is about to be — and each half repeats the row until it
                  is wider than the window, which is what stops a short row
                  running out. Only the first list is read out; everything after
                  it is the same names again, so it is hidden from assistive
                  technology. `pe-7` matches the internal `gap-x-7`, which is
                  what makes the seam invisible: see the note in `index.css`. */}
              {Array.from({ length: repeats * 2 }, (_, copy) => (
                <ul key={copy} className="flex shrink-0 items-center gap-x-7 pe-7" aria-hidden={copy > 0}>
                  {group.items.map((item) => (
                    // Not `item.src` alone: "On Premise" and "and many more"
                    // both have an empty one, since neither is a company with a
                    // logo to resolve.
                    <StackBadge key={item.name + item.src} item={item} />
                  ))}
                </ul>
              ))}
            </div>
          </div>
        </div>
        )
      })}
    </div>
  )
}
